// frontend/components/CrmActivities.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCrmActivities,
  fetchCrmContacts,
  createCrmActivity,
  updateCrmActivity,
  deleteCrmActivity,
} from "../features/crm/crmThunks.js";
import { X, Pencil, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import "../styles/crm/crmactivities.css";

const pad2 = (value) => String(value).padStart(2, "0");

const toDateInputValue = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = pad2(dateObj.getMonth() + 1);
  const day = pad2(dateObj.getDate());
  return `${year}-${month}-${day}`;
};

const toTimeInputValue = (dateObj) => {
  const hours = pad2(dateObj.getHours());
  const minutes = pad2(dateObj.getMinutes());
  return `${hours}:${minutes}`;
};

const parseToLocalDateTimeParts = (value) => {
  if (!value) return { dueDate: "", dueTime: "" };
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return { dueDate: "", dueTime: "" };
  return { dueDate: toDateInputValue(dateObj), dueTime: toTimeInputValue(dateObj) };
};

const formatDatetimeLocal = (value) => {
  if (!value) return "-";
  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return String(value);

  const date = dateObj.toLocaleDateString("es-ES");
  const time = dateObj.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
};

const getContactLabel = (contact) => {
  const name = String(contact?.full_name || "").trim();
  if (name) return name;
  return `Contacto #${contact?.id}`;
};

const sortByName = (a, b) => {
  const aName = String(a?.full_name || "").toLowerCase();
  const bName = String(b?.full_name || "").toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
};

const normalizeActivitiesPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const CrmActivities = () => {
  const dispatch = useDispatch();

  const crmState = useSelector((state) => state.crm || {});
  const activities = crmState.activities || [];
  const contacts = crmState.contacts || [];
  const loading = crmState.loading;
  const error = crmState.error;

  const currentUser = useSelector((state) => state.auth.user);

  // Filtros: asignación y estado
  const [assignedFilter, setAssignedFilter] = useState("mine"); // mine | all
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | completed | all

  // Formulario crear/editar
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [editingActivityId, setEditingActivityId] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    activityType: "call", // call | whatsapp | email | meeting | task
    description: "",
    dueDate: "",
    dueTime: "",
    isCompleted: false,
    contactId: "",
  });

  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Para evitar “pantallazo” / “recarga” al cambiar filtros:
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [cachedActivities, setCachedActivities] = useState([]);

  // Modal eliminar (confirm)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Cargar contactos (para el select)
  useEffect(() => {
    dispatch(fetchCrmContacts({}));
  }, [dispatch]);

  // ---------- CARGA / FILTRO BACKEND ----------
  useEffect(() => {
    let isCancelled = false;

    const assignedTo =
      assignedFilter === "mine" && currentUser ? currentUser.id : undefined;

    const isCompleted =
      statusFilter === "pending"
        ? false
        : statusFilter === "completed"
        ? true
        : undefined;

    dispatch(
      fetchCrmActivities({
        assignedTo,
        isCompleted,
      })
    ).then((resultAction) => {
      if (isCancelled) return;

      if (fetchCrmActivities.fulfilled.match(resultAction)) {
        setHasLoadedOnce(true);
        const nextActivities = normalizeActivitiesPayload(resultAction.payload);
        setCachedActivities(nextActivities);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [dispatch, currentUser, assignedFilter, statusFilter]);

  // UX: cerrar modal con ESC + bloquear scroll mientras esté abierto el modal
  useEffect(() => {
    if (!deleteOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closeDeleteModal();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteOpen]);

  const contactsSorted = useMemo(() => {
    return contacts.slice().sort(sortByName);
  }, [contacts]);

  const contactsById = useMemo(() => {
    const map = {};
    contacts.forEach((contact) => {
      map[String(contact.id)] = contact;
    });
    return map;
  }, [contacts]);

  // Mientras está cargando y el reducer “vacía” activities, mostramos cache (evita pantallazo)
  const visibleActivities = useMemo(() => {
    if (loading && (!activities || activities.length === 0) && hasLoadedOnce) {
      return cachedActivities;
    }
    return activities;
  }, [activities, cachedActivities, hasLoadedOnce, loading]);

  // ---------- FORM HELPERS ----------
  const resetForm = () => {
    setFormData({
      title: "",
      activityType: "call",
      description: "",
      dueDate: "",
      dueTime: "",
      isCompleted: false,
      contactId: "",
    });
    setFormError(null);
    setIsSubmitting(false);
    setEditingActivityId(null);
    setFormMode("create");
  };

  const handleOpenCreateForm = () => {
    resetForm();
    setFormMode("create");
    setShowForm(true);
  };

  const handleOpenEditForm = (activity) => {
    const { dueDate, dueTime } = parseToLocalDateTimeParts(activity.due_datetime);

    setFormData({
      title: activity.title || "",
      activityType: activity.activity_type || "call",
      description: activity.description || "",
      dueDate,
      dueTime,
      isCompleted: !!activity.is_completed,
      contactId: activity.contact ? String(activity.contact) : "",
    });

    setFormError(null);
    setIsSubmitting(false);
    setEditingActivityId(activity.id);
    setFormMode("edit");
    setShowForm(true);
  };

  const handleToggleComplete = async (activity) => {
    await dispatch(
      updateCrmActivity({
        activityId: activity.id,
        activityData: { is_completed: !activity.is_completed },
      })
    );
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildDueDatetimeIso = () => {
    if (!formData.dueDate || !formData.dueTime) return null;

    // Interpretar como hora LOCAL del usuario y enviar en ISO (UTC) para DRF
    const localDate = new Date(`${formData.dueDate}T${formData.dueTime}:00`);
    if (Number.isNaN(localDate.getTime())) return null;

    return localDate.toISOString();
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    if (!formData.title.trim()) {
      setFormError("El título es obligatorio.");
      return;
    }

    if (!formData.contactId) {
      setFormError("El contacto es obligatorio.");
      return;
    }

    const dueDatetimeIso = buildDueDatetimeIso();

    const basePayload = {
      title: formData.title.trim(),
      description: formData.description.trim() || "",
      activity_type: formData.activityType,
      due_datetime: dueDatetimeIso,
      is_completed: !!formData.isCompleted,
      contact: Number(formData.contactId),
    };

    setIsSubmitting(true);

    if (formMode === "create") {
      const payload = {
        ...basePayload,
        assigned_to: currentUser ? currentUser.id : null,
      };

      const resultAction = await dispatch(createCrmActivity(payload));

      if (createCrmActivity.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo crear la actividad." };
        setFormError(err.detail || "No se pudo crear la actividad.");
      }
    } else if (formMode === "edit" && editingActivityId) {
      const resultAction = await dispatch(
        updateCrmActivity({
          activityId: editingActivityId,
          activityData: basePayload,
        })
      );

      if (updateCrmActivity.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo actualizar la actividad." };
        setFormError(err.detail || "No se pudo actualizar la actividad.");
      }
    }

    setIsSubmitting(false);
  };

  const getActivityTypeLabel = (type) => {
    if (type === "call") return "Llamada";
    if (type === "whatsapp") return "WhatsApp";
    if (type === "email") return "Email";
    if (type === "meeting") return "Visita";
    if (type === "task") return "Tarea";
    return type || "-";
  };

  // ---------- DELETE MODAL ----------
  const openDeleteModal = (activity) => {
    setDeleteError(null);
    setActivityToDelete(activity);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setActivityToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!activityToDelete?.id) return;

    setDeleteError(null);
    setDeleting(true);

    const resultAction = await dispatch(deleteCrmActivity(activityToDelete.id));

    setDeleting(false);

    if (deleteCrmActivity.fulfilled.match(resultAction)) {
      closeDeleteModal();
      return;
    }

    const err = resultAction.payload || { detail: "No se pudo eliminar la actividad." };
    setDeleteError(err.detail || "No se pudo eliminar la actividad.");
  };

  if (!hasLoadedOnce && loading && (!visibleActivities || visibleActivities.length === 0)) {
    return <div>Cargando actividades...</div>;
  }

  if (error && !loading && (!visibleActivities || visibleActivities.length === 0)) {
    return <div>Error al cargar actividades: {error.detail || "Error"}</div>;
  }

  return (
    <div className="crm-activities-container">
      <div className="crm-activities-header">
        <div>
          <h2>Actividades</h2>
          <p className="crm-activities-subtitle">
            Llamadas, mensajes y tareas ligadas a tus leads y pacientes.
          </p>
        </div>

        <div className="crm-activities-actions">
          <div className="crm-activity-filter-group">
            <span className="crm-activity-filter-label">Asignación:</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-btn ${assignedFilter === "mine" ? "is-active" : ""}`}
                onClick={() => setAssignedFilter("mine")}
              >
                Mis actividades
              </button>
              <button
                type="button"
                className={`segmented-btn ${assignedFilter === "all" ? "is-active" : ""}`}
                onClick={() => setAssignedFilter("all")}
              >
                Todas
              </button>
            </div>
          </div>

          <div className="crm-activity-filter-group">
            <span className="crm-activity-filter-label">Estado:</span>
            <div className="segmented">
              <button
                type="button"
                className={`segmented-btn ${statusFilter === "pending" ? "is-active" : ""}`}
                onClick={() => setStatusFilter("pending")}
              >
                Pendientes
              </button>
              <button
                type="button"
                className={`segmented-btn ${statusFilter === "completed" ? "is-active" : ""}`}
                onClick={() => setStatusFilter("completed")}
              >
                Completadas
              </button>
              <button
                type="button"
                className={`segmented-btn ${statusFilter === "all" ? "is-active" : ""}`}
                onClick={() => setStatusFilter("all")}
              >
                Todas
              </button>
            </div>
          </div>

          <button type="button" className="btn-primary btn-sm" onClick={handleOpenCreateForm}>
            Nueva actividad
          </button>

          {loading ? <div className="crm-activities-inline-status">Actualizando…</div> : null}
        </div>
      </div>

      {showForm && (
        <div className="crm-activity-form-wrapper">
          <form className="crm-activity-form" onSubmit={handleFormSubmit}>
            <h3>{formMode === "create" ? "Nueva actividad" : "Editar actividad"}</h3>

            <div className="crm-activity-form-row">
              <div className="crm-activity-field">
                <label htmlFor="title">Título</label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Ej: Llamar para resolver dudas"
                />
              </div>

              <div className="crm-activity-field">
                <label htmlFor="activityType">Tipo</label>
                <select
                  id="activityType"
                  name="activityType"
                  value={formData.activityType}
                  onChange={handleFormChange}
                >
                  <option value="call">Llamada</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="meeting">Visita</option>
                  <option value="task">Tarea</option>
                </select>
              </div>
            </div>

            <div className="crm-activity-form-row">
              <div className="crm-activity-field">
                <label htmlFor="dueDate">Fecha</label>
                <input
                  id="dueDate"
                  name="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={handleFormChange}
                />
              </div>

              <div className="crm-activity-field">
                <label htmlFor="dueTime">Hora</label>
                <input
                  id="dueTime"
                  name="dueTime"
                  type="time"
                  value={formData.dueTime}
                  onChange={handleFormChange}
                />
              </div>

              <div className="crm-activity-field crm-activity-field-inline">
                <label htmlFor="isCompleted">Completada</label>
                <input
                  id="isCompleted"
                  name="isCompleted"
                  type="checkbox"
                  checked={formData.isCompleted}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            <div className="crm-activity-form-row">
              <div className="crm-activity-field">
                <label htmlFor="contactId">Contacto</label>
                <select
                  id="contactId"
                  name="contactId"
                  value={formData.contactId}
                  onChange={handleFormChange}
                >
                  <option value="" disabled>
                    Selecciona un contacto…
                  </option>
                  {contactsSorted.map((contact) => (
                    <option key={contact.id} value={String(contact.id)}>
                      {getContactLabel(contact)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="crm-activity-form-row">
              <div className="crm-activity-field crm-activity-field-notes">
                <label htmlFor="description">Notas</label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Notas sobre la llamada o la tarea..."
                />
              </div>
            </div>

            {formError ? <div className="crm-activity-form-error">{formError}</div> : null}

            <div className="crm-activity-form-actions">
              <button type="submit" className="btn-primary btn-sm" disabled={isSubmitting}>
                {isSubmitting
                  ? "Guardando..."
                  : formMode === "create"
                  ? "Crear actividad"
                  : "Guardar cambios"}
              </button>

              <button
                type="button"
                className="ghost btn-sm"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {visibleActivities.length === 0 ? (
        <p>No hay actividades para los filtros seleccionados.</p>
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Tipo</th>
              <th>Fecha/Hora</th>
              <th>Estado</th>
              <th>Contacto</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleActivities.map((activity) => {
              const contact = activity.contact ? contactsById[String(activity.contact)] : null;

              return (
                <tr key={activity.id}>
                  <td>{activity.title}</td>
                  <td>{getActivityTypeLabel(activity.activity_type)}</td>
                  <td>{formatDatetimeLocal(activity.due_datetime)}</td>
                  <td>{activity.is_completed ? "Completada" : "Pendiente"}</td>
                  <td>{contact ? contact.full_name : activity.contact || "-"}</td>
                  <td>
                    <div className="crm-activities-action-icons">
                      <button
                        type="button"
                        className="crm-icon-btn"
                        onClick={() => handleOpenEditForm(activity)}
                        title="Editar"
                        aria-label="Editar actividad"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        type="button"
                        className="crm-icon-btn"
                        onClick={() => handleToggleComplete(activity)}
                        title={activity.is_completed ? "Marcar pendiente" : "Marcar completada"}
                        aria-label={activity.is_completed ? "Marcar pendiente" : "Marcar completada"}
                      >
                        {activity.is_completed ? <RotateCcw size={18} /> : <CheckCircle2 size={18} />}
                      </button>

                      <button
                        type="button"
                        className="crm-icon-btn danger"
                        onClick={() => openDeleteModal(activity)}
                        title="Eliminar"
                        aria-label="Eliminar actividad"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* MODAL ELIMINAR */}
      {deleteOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar actividad"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar actividad</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDeleteModal}
                disabled={deleting}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar{" "}
                <strong>{activityToDelete?.title || `Actividad #${activityToDelete?.id}`}</strong>?
                <div className="ui-delete-subhelp">Esta acción no se puede deshacer.</div>
              </div>

              {deleteError ? <div className="ui-modal-error">{deleteError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmActivities;